import React, { useContext, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import delay from 'delay'
import anime from 'animejs'
import { useSearchParams } from 'react-router-dom'
import { Button, Collapse, useTheme } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { AuthContext } from '../../contexts/Auth'
import { usePreserveQueryParamsNavigate } from '../../hooks/usePreserveQueryParamsNavigate'
import Text from '../Text'
import Logo from '../Logo'
import CreditoGLA from '../CreditoGLA'
import CircularProgressCustom from '../CircularProgressCustom'
import LinearProgressCustom from '../LinearProgressCustom'
import { obterBannerLogin, DESTAQUES } from '../../libs/bannerLogin'

// Quanto a saudação "Bem vindo" fica na tela antes de abrir o painel.
const TEMPO_SAUDACAO = 600

// O painel da esquerda é escuro nos dois temas de propósito: é ele que dá liga
// entre o ciano da marca e o resto da tela. O layout antigo jogava uma faixa
// chapada de ciano de 300px sobre o fundo cinza, e era daí que vinha a cor
// destoando — pior ainda no tema escuro.
const FUNDO_PAINEL = 'linear-gradient(155deg, #0b1f28 0%, #0e2c39 55%, #00404f 100%)'

// A divisão só acontece a partir de 1024px. Entre 768 e 1023 o painel ficaria
// com pouco mais de 300px e a chamada quebraria em quatro linhas — abaixo
// disso a tela empilha, que lê melhor.

function prefereMenosMovimento() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export default function SigninForm() {
  const [queryParameters] = useSearchParams()
  const { signin } = useContext(AuthContext)
  const navigate = usePreserveQueryParamsNavigate()
  const theme = useTheme()
  const formContainerRef = useRef()
  const welcomeMessageRef = useRef()
  const animar = useRef(!prefereMenosMovimento())

  const [api, setApi] = useState('')
  const [secret, setSecret] = useState('')
  const [mostrarSecret, setMostrarSecret] = useState(false)
  const [campoFocado, setCampoFocado] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isTryingSigninByParameterUrl, setIsTryingSigninByParameterUrl] = useState(true)
  const [loginSuccess, setLoginSuccess] = useState(false)
  const [banner, setBanner] = useState(null)

  const handleSignin = async (api, secret) => {
    try {
      setErrorMessage('')
      setIsLoading(true)
      await signin(api, secret)
      handleSaveCredentialsLocalStorage(api, secret)
      setLoginSuccess(true)
      setIsLoading(false)
      handleFormAnimationReverse()
      handleWelcomeAnimation()
      // Só o tempo da saudação não piscar. Antes eram quase seis segundos de
      // animação entre a credencial ser aceita e o painel abrir.
      await delay(TEMPO_SAUDACAO)
      navigate('/')
    } catch (error) {
      console.error(error.response)
      setIsLoading(false)
      setLoginSuccess(false)

      if(error?.response?.data?.error === 'Não foi possível encontrar a instância') {
        setErrorMessage('Credenciais inválidas. Confira a api e o secret e tente de novo.')
        return
      }

      setErrorMessage('Não conseguimos entrar agora. Tente novamente em alguns instantes.')
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if(isLoading || loginSuccess) return
    handleSignin(api, secret)
  }

  const handleTrySigninByUrlParameters = async () => {
    const api = queryParameters.get('api')
    const secret = queryParameters.get('secret')
    // Sem credencial na URL não há o que tentar: o formulário aparece na hora,
    // em vez de segurar a tela em "Carregando" por dois segundos à toa.
    if(!api || !secret) {
      setIsTryingSigninByParameterUrl(false)
      return
    }
    setIsTryingSigninByParameterUrl(true)
    await handleSignin(api, secret)
    setIsTryingSigninByParameterUrl(false)
  }

  const handleSaveCredentialsLocalStorage = (api, secret) => {
    const queryApi = queryParameters.get('api')
    const querySecret = queryParameters.get('secret')
    if(queryApi || querySecret) {
      return
    }
    window.localStorage.setItem('api', api)
    window.localStorage.setItem('secret', secret)
  }

  const handleFormAnimation = async () => {
    if(!animar.current) return
    await delay(150)
    if(!formContainerRef.current) return
    anime({
      targets: formContainerRef.current.children,
      translateY: [24, 0],
      opacity: [0, 1],
      delay: anime.stagger(50),
      easing: 'easeOutExpo'
    })
  }

  const handleFormAnimationReverse = () => {
    if(!animar.current || !formContainerRef.current) return
    anime({
      targets: formContainerRef.current.children,
      translateY: [0, 24],
      opacity: [1, 0],
      delay: anime.stagger(40),
      easing: 'easeInExpo'
    })
  }

  const handleWelcomeAnimation = () => {
    if(!animar.current || !welcomeMessageRef.current) return
    anime({
      targets: welcomeMessageRef.current.children,
      translateY: [24, 0],
      opacity: [0, 1],
      delay: anime.stagger(50),
      easing: 'easeOutExpo'
    })
  }

  useEffect(() => {
    if(isTryingSigninByParameterUrl) return
    handleFormAnimation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTryingSigninByParameterUrl])

  useEffect(() => {
    handleTrySigninByUrlParameters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setApi(queryParameters.get('api') || '')
    setSecret(queryParameters.get('secret') || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Um banner que falha nunca pode impedir alguém de entrar.
    obterBannerLogin()
      .then(setBanner)
      .catch(() => setBanner(null))
  }, [])

  // Enquanto tenta entrar pela credencial da URL, a tela fica só com a marca.
  if(isTryingSigninByParameterUrl && !loginSuccess) {
    return (
      <div
        className="min-h-full flex flex-col justify-center items-center gap-6 p-6"
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <Logo tamanho="grande" assinatura />
        <div className="flex flex-col items-center">
          <LinearProgressCustom className="w-[68px]" />
          <Text className="font-black text-xs mt-1" style={{ color: theme.palette.primary.main }}>
            Carregando
          </Text>
        </div>
      </div>
    )
  }

  const bannerAtivo = banner && banner.ativo
  const temBotao = bannerAtivo && banner.botaoUrl && banner.botaoTexto
  const oculto = animar.current ? 'opacity-0' : ''

  // Um campo por vez fica com o anel de foco — o mesmo tratamento da navegação.
  const estiloCaixa = (nome) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${campoFocado === nome ? theme.palette.primary.main : theme.palette.divider}`,
    boxShadow: campoFocado === nome ? `0 0 0 3px ${theme.palette.primary.main}33` : 'none',
    transition: 'border-color 150ms, box-shadow 150ms'
  })

  return (
    <div
      className="min-h-full flex flex-col lg:flex-row"
      style={{ backgroundColor: theme.palette.background.default }}
    >
      {/* saudação após entrar */}
      <div
        className={clsx(
          loginSuccess ? '' : 'hidden pointer-events-none',
          'fixed z-[999] inset-0 flex justify-center items-center p-6'
        )}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <div ref={welcomeMessageRef} className="flex flex-col justify-center items-center text-center">
          <span className={clsx(oculto, 'inline-block')}>
            <CheckCircleIcon style={{ fontSize: 44, color: theme.palette.primary.main }} />
          </span>
          <span
            className={clsx(oculto, 'inline-block mt-2 font-medium')}
            style={{ color: theme.palette.text.primary, fontSize: 'clamp(1.6rem, 5vw, 3rem)' }}
          >
            Bem vindo
          </span>
          <div className={clsx(oculto, 'mt-6')}>
            <CircularProgressCustom size={30} />
          </div>
        </div>
      </div>

      {/* ---------- painel da marca e do banner ---------- */}
      <div
        className="relative flex-none w-full lg:w-[46%] xl:w-[42%] lg:max-w-[620px] overflow-hidden flex flex-col lg:min-h-screen px-[clamp(1.25rem,4vw,3.5rem)] py-[clamp(1.5rem,4vh,3.5rem)] gap-6"
        style={{
          background: FUNDO_PAINEL,
          backgroundImage: bannerAtivo && banner.imagemUrl
            ? `linear-gradient(155deg, rgba(11,31,40,.92) 0%, rgba(0,64,79,.82) 100%), url(${banner.imagemUrl})`
            : FUNDO_PAINEL,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div
          aria-hidden="true"
          className="absolute -right-24 -bottom-24 w-[320px] h-[320px] rounded-full pointer-events-none"
          style={{ backgroundColor: '#00ccff', opacity: 0.16, filter: 'blur(60px)' }}
        />

        {/* marca: centralizada quando empilhado, à esquerda na tela dividida */}
        <div className="relative flex justify-center lg:justify-start">
          <Logo tamanho="grande" white assinatura />
        </div>

        {/* o banner ocupa o meio do painel na tela dividida */}
        <div className="relative flex-1 flex flex-col justify-center text-center lg:text-left">
          {bannerAtivo && (
            <div>
              <p
                className="text-white font-black leading-tight m-0 text-balance"
                style={{ fontSize: 'clamp(1.3rem, 1.1vw + 1rem, 2.3rem)' }}
              >
                {banner.titulo}
              </p>
              {banner.subtitulo && (
                <p
                  className="text-white opacity-80 leading-snug mt-3 mx-auto lg:mx-0 max-w-[46ch] m-0 text-balance"
                  style={{ fontSize: 'clamp(0.8rem, 0.3vw + 0.72rem, 1rem)' }}
                >
                  {banner.subtitulo}
                </p>
              )}
              {temBotao && (
                <a
                  href={banner.botaoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="foco-navegacao inline-flex items-center justify-center mt-6 rounded-lg px-6 h-[46px] font-black text-sm"
                  style={{ backgroundColor: '#00ccff', color: '#00252f' }}
                >
                  {banner.botaoTexto}
                </a>
              )}
            </div>
          )}

          {/* As frases só entram na tela dividida: empilhadas, empurrariam o
              formulário para baixo da dobra no celular. */}
          <ul className="hidden lg:flex flex-col gap-3 mt-10 m-0 p-0 list-none">
            {DESTAQUES.map(destaque => (
              <li key={destaque} className="flex items-start gap-2">
                <CheckCircleIcon style={{ fontSize: 17, color: '#00ccff', marginTop: 3, flex: 'none' }} />
                <span className="text-white opacity-90 leading-snug text-sm">{destaque}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* crédito: no pé do painel só quando a tela é dividida */}
        {/* No painel o crédito empilha à esquerda: lado a lado ele não cabe na
            largura da coluna e quebra no meio da frase. */}
        <div className="relative hidden lg:block">
          <CreditoGLA claro className="lg:flex-col lg:items-start lg:gap-1.5" />
        </div>
      </div>

      {/* ---------- formulário ---------- */}
      <div className="flex-1 flex flex-col items-center justify-center gap-8 px-[clamp(1.25rem,4vw,3rem)] py-[clamp(2rem,5vh,4rem)]">
        <form
          ref={formContainerRef}
          onSubmit={handleSubmit}
          className="w-full max-w-[400px]"
          noValidate
        >
          <div className={clsx(oculto, 'mb-7 text-center')}>
            <Text className="font-black" style={{ fontSize: 'clamp(1.4rem, 1vw + 1.1rem, 1.9rem)' }}>
              Entrar no painel
            </Text>
            <Text className="text-sm opacity-70 mt-1">
              Use a api e o secret que você recebeu.
            </Text>
          </div>

          <div className={oculto}>
            <Collapse in={!!errorMessage} timeout="auto" unmountOnExit>
              <div
                className="flex items-start gap-2 rounded-lg p-3 mb-5"
                style={{
                  backgroundColor: `${theme.palette.error.main}1A`,
                  color: theme.palette.error.main
                }}
                role="alert"
              >
                <ErrorOutlineIcon style={{ fontSize: 19, flex: 'none' }} />
                <span className="text-sm font-medium leading-snug">{errorMessage}</span>
              </div>
            </Collapse>
          </div>

          <div className={clsx(oculto, 'mb-4')}>
            <label
              htmlFor="campo-api"
              className="block text-[11px] font-black uppercase mb-2"
              style={{ color: theme.palette.text.secondary, letterSpacing: '0.1em' }}
            >
              Api
            </label>
            <div
              className="flex items-center gap-2 rounded-lg px-3 h-[48px]"
              style={estiloCaixa('api')}
            >
              <VpnKeyIcon style={{ fontSize: 18, color: theme.palette.text.secondary, flex: 'none' }} />
              <input
                id="campo-api"
                name="api"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                className="flex-1 min-w-0 h-full bg-transparent outline-none text-sm"
                style={{ color: theme.palette.text.primary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                placeholder="api-xxxxxxxxxxx"
                onFocus={() => setCampoFocado('api')}
                onBlur={() => setCampoFocado('')}
                onChange={e => setApi(e.target.value)}
                value={api}
              />
            </div>
          </div>

          <div className={clsx(oculto, 'mb-7')}>
            <label
              htmlFor="campo-secret"
              className="block text-[11px] font-black uppercase mb-2"
              style={{ color: theme.palette.text.secondary, letterSpacing: '0.1em' }}
            >
              Secret
            </label>
            <div
              className="flex items-center gap-2 rounded-lg px-3 h-[48px]"
              style={estiloCaixa('secret')}
            >
              <LockIcon style={{ fontSize: 18, color: theme.palette.text.secondary, flex: 'none' }} />
              <input
                id="campo-secret"
                name="secret"
                type={mostrarSecret ? 'text' : 'password'}
                autoComplete="current-password"
                className="flex-1 min-w-0 h-full bg-transparent outline-none text-sm"
                style={{ color: theme.palette.text.primary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                placeholder="••••••••••••"
                onFocus={() => setCampoFocado('secret')}
                onBlur={() => setCampoFocado('')}
                onChange={e => setSecret(e.target.value)}
                value={secret}
              />
              <button
                type="button"
                onClick={() => setMostrarSecret(!mostrarSecret)}
                aria-label={mostrarSecret ? 'Esconder o secret' : 'Mostrar o secret'}
                className="foco-navegacao flex items-center justify-center w-8 h-8 -mr-1 rounded flex-none"
                style={{ color: theme.palette.text.secondary }}
              >
                {mostrarSecret
                  ? <VisibilityOffIcon style={{ fontSize: 19 }} />
                  : <VisibilityIcon style={{ fontSize: 19 }} />}
              </button>
            </div>
          </div>

          <div className={oculto}>
            <Button
              type="submit"
              disabled={isLoading || loginSuccess}
              className="h-[50px]"
              variant="contained"
              fullWidth
              size="large"
            >
              <span className="font-black text-white">
                {isLoading ? 'VALIDANDO CREDENCIAIS' : 'ENTRAR'}
              </span>
            </Button>
          </div>

          <div className={clsx(oculto, 'mt-5')}>
            <Text className="text-xs opacity-60 text-center leading-snug">
              Não sabe suas credenciais? Fale com quem contratou o serviço.
            </Text>
          </div>
        </form>

        {/* Crédito no pé da página quando a tela está empilhada. Fica mais
            largo que o formulário porque lado a lado ele não cabe em 400px. */}
        <div className="w-full max-w-[560px] lg:hidden">
          <CreditoGLA />
        </div>
      </div>
    </div>
  )
}
